import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import { Crosshair } from "@/components/Crosshair";
import { timeAgo } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  MailCheck, Briefcase, Check, X, ExternalLink, MailWarning, EyeOff, RotateCcw,
  ShieldAlert, Clock, ChevronDown, ChevronRight, FlaskConical, FileText, FileCheck2, ListChecks,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { BusinessWaitlistTable } from "@/components/admin/BusinessWaitlistTable";

const BLOCKER_TONE = {
  email_unverified: "border-[#d3a24e]/50 text-[#d3a24e]",
  id_unverified: "border-[#d3a24e]/50 text-[#d3a24e]",
  id_processing: "border-teal-300/50 text-teal-300",
  id_failed: "border-blaze/60 text-blaze",
  payout_incomplete: "border-volt/50 text-volt",
};
const BLOCKER_LABEL = {
  email_unverified: "Email not verified",
  id_unverified: "ID not verified",
  id_processing: "ID processing",
  id_failed: "ID failed",
  payout_incomplete: "Payout not set up",
};
const ID_TONE = {
  verified: "border-olive/60 text-olive-bright",
  processing: "border-teal-300/50 text-teal-300",
  failed: "border-blaze/60 text-blaze",
  unverified: "border-sniper text-muted-foreground",
};

const FFL_TYPE_SHORT = {
  "01": "01 Dealer", "02": "02 Pawnbroker", "03": "03 C&R", "06": "06 Ammo Mfr",
  "07": "07 Mfr", "08": "08 Importer", "09": "09 DD Dealer", "10": "10 DD Mfr", "11": "11 DD Importer",
};
const ACCT_TYPE_LABEL = {
  firearms_accessories: "Firearms & Accessories",
  accessories_only: "Accessories Only",
};
const BLOCKER_FILTERS = [
  { value: "", label: "All blockers" },
  { value: "email_unverified", label: "Email not verified" },
  { value: "id", label: "Any ID issue" },
  { value: "payout_incomplete", label: "Payout not set up" },
];

const COOLDOWN_HOURS = 72;

function BlockerChips({ blockers, uid }) {
  if (!blockers?.length) {
    return <span className="font-mono text-[10px] uppercase text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1" data-testid={`pending-blockers-${uid}`}>
      {blockers.map((b) => (
        <span key={b} data-testid={`blocker-chip-${b}-${uid}`}
          className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border ${BLOCKER_TONE[b] || "border-sniper text-muted-foreground"}`}>
          {BLOCKER_LABEL[b] || b}
        </span>
      ))}
    </div>
  );
}

const DRow = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3 py-0.5">
    <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">{k}</span>
    <span className="text-xs text-foreground text-right font-body break-words">{v || "—"}</span>
  </div>
);

function BizAppDetail({ app, ffl, onViewPrimaryDoc, onViewAddlDoc }) {
  if (!app) return null;
  const fflTypes = app.ffl_types || [];
  const sotClasses = app.sot_classes || (app.sot_class ? [app.sot_class] : []);
  const addl = app.additional_licenses || [];
  const premises = [app.premises_address, app.city, app.state, app.zip_code].filter(Boolean).join(", ");
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid={`biz-detail-${app.application_id}`}>
      {/* Business + owner */}
      <div className="border border-sniper bg-black/30 p-3 space-y-0.5">
        <p className="font-mono text-[10px] uppercase tracking-wide text-olive-bright mb-1.5">Business &amp; Owner</p>
        <DRow k="Legal name" v={app.legal_business_name || app.business_name} />
        <DRow k="DBA" v={app.dba} />
        <DRow k="Tax ID / EIN" v={app.tax_id_ein} />
        <DRow k="Ownership" v={app.form_of_ownership} />
        <DRow k="Owner" v={app.owner_name ? `${app.owner_name}${app.owner_title ? ` · ${app.owner_title}` : ""}` : ""} />
        <DRow k="Premises" v={premises} />
        <DRow k="Years" v={app.years_in_business} />
        <DRow k="Phone" v={app.business_phone} />
        <DRow k="Email" v={app.business_email || app.email} />
        <DRow k="Website" v={app.website} />
      </div>

      {/* FFL / SOT */}
      <div className="border border-sniper bg-black/30 p-3 space-y-2">
        <p className="font-mono text-[10px] uppercase tracking-wide text-olive-bright">FFL / SOT</p>
        <DRow k="Account type" v={ACCT_TYPE_LABEL[app.account_type_requested] || app.account_type_requested} />
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1">FFL types held</p>
          {fflTypes.length ? (
            <div className="flex flex-wrap gap-1" data-testid={`biz-ffl-types-${app.application_id}`}>
              {fflTypes.map((t) => (
                <span key={t} data-testid={`biz-ffl-type-${app.application_id}-${t}`} className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-olive/60 text-olive-bright">{FFL_TYPE_SHORT[t] || t}</span>
              ))}
            </div>
          ) : <span className="font-mono text-[10px] uppercase text-muted-foreground">None</span>}
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1">SOT class</p>
          {sotClasses.length ? (
            <div className="flex flex-wrap gap-1" data-testid={`biz-sot-classes-${app.application_id}`}>
              {sotClasses.map((s) => (
                <span key={s} className="font-mono text-[10px] uppercase px-1.5 py-0.5 border border-teal-300/50 text-teal-300">{s}</span>
              ))}
            </div>
          ) : <span className="font-mono text-[10px] uppercase text-muted-foreground">None</span>}
        </div>
        <DRow k="% sales firearms" v={app.pct_sales_firearms} />
        {ffl ? (
          <div className="pt-1 border-t border-sniper/60">
            <DRow k="FFL #" v={ffl.ffl_number} />
            <DRow k="Type" v={ffl.ffl_type ? (FFL_TYPE_SHORT[ffl.ffl_type] || ffl.ffl_type) : ""} />
            <DRow k="Licensee" v={ffl.licensee_name} />
            <DRow k="Expires" v={ffl.expiration_date} />
            <DRow k="FFL status" v={ffl.status} />
            {ffl.has_document && ffl.license_id && (
              <button type="button" onClick={() => onViewPrimaryDoc(ffl.license_id)} data-testid={`biz-view-ffl-doc-${app.application_id}`}
                className="mt-1.5 inline-flex items-center gap-1 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[10px] px-2 py-1 transition-colors">
                <FileText className="w-3 h-3" /> View FFL document
              </button>
            )}
          </div>
        ) : (
          <p className="font-mono text-[10px] uppercase text-muted-foreground pt-1 border-t border-sniper/60">No FFL document on file</p>
        )}
      </div>

      {/* Additional licenses */}
      {addl.length > 0 && (
        <div className="border border-sniper bg-black/30 p-3 space-y-2 md:col-span-2" data-testid={`biz-addl-${app.application_id}`}>
          <p className="font-mono text-[10px] uppercase tracking-wide text-olive-bright flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5" /> Additional licenses ({addl.length})</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {addl.map((l) => (
              <div key={l.license_id} data-testid={`biz-addl-row-${l.license_id}`} className="border border-sniper px-2.5 py-2">
                <p className="text-xs text-foreground font-body">{l.license_type}</p>
                <p className="font-mono text-[10px] text-muted-foreground mt-0.5">{[l.license_number, l.issuing_authority, l.expiration_date].filter(Boolean).join(" · ") || "No details"}</p>
                {l.has_document ? (
                  <button type="button" onClick={() => onViewAddlDoc(app.application_id, l.license_id)} data-testid={`biz-addl-view-${l.license_id}`}
                    className="mt-1.5 inline-flex items-center gap-1 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[10px] px-2 py-1 transition-colors">
                    <FileCheck2 className="w-3 h-3" /> View document
                  </button>
                ) : (
                  <p className="font-mono text-[10px] uppercase text-muted-foreground mt-1">No document</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function withinCooldown(iso) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return Date.now() - t < COOLDOWN_HOURS * 3600 * 1000;
}

// Full "N days ago" — the abbreviation "8d ago" saves no space here and costs legibility.
function daysAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const d = Math.floor(ms / 86400000);
  if (d <= 0) return "today";
  return d === 1 ? "1 day ago" : `${d} days ago`;
}

// Pending Verification palette. Global rule: pure #FFFFFF is banned on dark surfaces; ceiling
// is #DAD6CE. Readability comes from raising the SURFACE, not brightening the type — only the
// hovered row is ever at full strength.
const PV = {
  surface: "#1C1E21", border: "#3D3F43", divider: "#2E3033", bar: "#25272B",
  gold: "#D3A24E", amber: "#C2600F",           // left-border accents (ID gold / email amber)
  heading: "#DAD6CE", name: "#C6C8CC", handle: "#76797D", email: "#8E9195",
  waiting: "#8E9195", date: "#6E7175", btnText: "#A9ABAF", btnBorder: "#494B4F",
  nameHover: "#E0E2E6", emailHover: "#B4B6BA", disabled: "#76797D", oliveText: "#0B0C0B",
};

// One row. Only the hovered row lifts to full strength (name + email); nothing else changes.
function PVRow({ r, tone, onRemind, onOpenUser, onDismiss, onRestore, busy }) {
  const [hover, setHover] = useState(false);
  const cooling = withinCooldown(r.last_reminder_at);
  const acting = busy === r.user_id;
  const showName = r.display_name && r.display_name.toLowerCase() !== (r.username || "").toLowerCase();
  const outline = { color: PV.btnText, border: `1px solid ${PV.btnBorder}` };
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4 px-4 py-3.5 transition-colors"
      style={{ borderLeft: `3px solid ${tone}`, borderBottom: `1px solid ${PV.divider}`, background: hover ? "#212327" : "transparent" }}
      data-testid={`pending-row-${r.user_id}`}>
      <div className="min-w-0 sm:flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          {showName && <span className="font-body text-sm break-words" style={{ color: hover ? PV.nameHover : PV.name }} data-testid={`pending-name-${r.user_id}`}>{r.display_name}</span>}
          <span className="font-mono text-[12px] break-words" style={{ color: PV.handle }}>@{r.username}</span>
        </div>
        <div className="font-mono text-[11px] break-all mt-0.5" style={{ color: hover ? PV.emailHover : PV.email }}>{r.email}</div>
        {r.dismissed && (
          <div className="font-mono text-[10px] uppercase mt-1" style={{ color: PV.date }} data-testid={`pending-dismissed-info-${r.user_id}`}>
            Dismissed by {r.dismissed_by} · {daysAgo(r.dismissed_at)}{r.dismissed_reason ? ` · ${r.dismissed_reason}` : ""}
          </div>
        )}
      </div>
      <div className="shrink-0 whitespace-nowrap text-left sm:text-right pt-0.5">
        <div className="font-mono text-[12px]" style={{ color: PV.waiting }}>{daysAgo(r.created_at)}</div>
        <div className="font-mono text-[10px]" style={{ color: PV.date }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</div>
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
        {!r.dismissed && (cooling ? (
          <span className="font-mono text-[10px] uppercase px-2 py-1.5" style={{ color: PV.disabled }} data-testid={`pending-reminded-${r.user_id}`}>
            Reminded {daysAgo(r.last_reminder_at)}
          </span>
        ) : (
          <button onClick={() => onRemind(r.user_id)} disabled={acting} data-testid={`pending-remind-${r.user_id}`}
            className="inline-flex items-center gap-1 bg-olive hover:bg-olive-bright font-mono uppercase text-[10px] px-3 py-1.5 transition-colors" style={{ color: PV.oliveText }}>
            <MailWarning className="w-3.5 h-3.5" /> Remind
          </button>
        ))}
        <button onClick={() => onOpenUser(r.user_id)} data-testid={`pending-open-user-${r.user_id}`}
          className="inline-flex items-center gap-1 font-mono uppercase text-[10px] px-3 py-1.5 transition-colors hover:brightness-125" style={outline}>
          <ExternalLink className="w-3.5 h-3.5" /> Open User
        </button>
        {r.dismissed ? (
          <button onClick={() => onRestore(r.user_id)} disabled={acting} data-testid={`pending-restore-${r.user_id}`}
            className="inline-flex items-center gap-1 font-mono uppercase text-[10px] px-3 py-1.5 transition-colors hover:brightness-125" style={outline}>
            <RotateCcw className="w-3.5 h-3.5" /> Restore
          </button>
        ) : (
          <button onClick={() => onDismiss(r)} disabled={acting} data-testid={`pending-dismiss-${r.user_id}`}
            className="inline-flex items-center gap-1 font-mono uppercase text-[10px] px-3 py-1.5 transition-colors hover:brightness-125" style={{ color: PV.date, border: `1px solid ${PV.btnBorder}` }}>
            <EyeOff className="w-3.5 h-3.5" /> Dismiss
          </button>
        )}
      </div>
    </div>
  );
}

// One blocker group: heading + count + description, an action bar with REMIND ALL, then rows.
function PVGroup({ testid, title, desc, rowsData, onRemindAll, remindingAll, children }) {
  const active = rowsData.filter((r) => !r.dismissed);
  const remindable = active.filter((r) => !withinCooldown(r.last_reminder_at)).length;
  const onCooldown = active.length - remindable;
  return (
    <section className="mt-7" data-testid={testid}>
      <div className="flex items-baseline gap-3 flex-wrap">
        <h2 className="font-heading text-2xl font-bold uppercase tracking-tight" style={{ color: PV.heading }}>{title}</h2>
        <span className="font-mono text-sm" style={{ color: PV.waiting }} data-testid={`${testid}-count`}>{active.length} waiting</span>
      </div>
      <p className="font-body text-sm mt-1" style={{ color: PV.email }}>{desc}</p>
      <div className="mt-3" style={{ background: PV.surface, border: `1px solid ${PV.border}` }}>
        <div className="flex items-center justify-between px-4 py-2.5" style={{ background: PV.bar, borderBottom: `1px solid ${PV.border}` }}>
          <span className="font-mono text-[10px] uppercase" style={{ color: PV.date }}>
            {remindable} can be reminded now{onCooldown > 0 ? ` · ${onCooldown} on cooldown` : ""}
          </span>
          <button onClick={onRemindAll} disabled={remindingAll || remindable === 0} data-testid={`${testid}-remind-all`}
            className="inline-flex items-center gap-1.5 font-mono uppercase text-[10px] px-3 py-1.5 transition-colors hover:brightness-110"
            style={remindable === 0 ? { color: PV.disabled, border: `1px solid ${PV.btnBorder}` } : { color: PV.oliveText, background: "#5c6b34" }}>
            <MailWarning className="w-3.5 h-3.5" /> {remindingAll ? "Reminding…" : "Remind all"}
          </button>
        </div>
        {rowsData.length === 0 ? (
          <div className="px-4 py-8 text-center font-mono uppercase text-xs" style={{ color: PV.date }} data-testid={`${testid}-empty`}>None waiting.</div>
        ) : children}
      </div>
    </section>
  );
}

// mode: "verification" (Individual — self-blocked nudge list) | "business" (dealer apps waiting on staff)
export default function PendingUsers({ mode = "verification" }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isBiz = mode === "business";
  const tab = isBiz ? "business" : "individual";
  const [blocker, setBlocker] = useState("");
  const [showDismissed, setShowDismissed] = useState(false);
  const [bizTab, setBizTab] = useState("applications"); // business mode: "applications" | "waitlist"
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ individual: 0, business: 0, individual_dismissed: 0, business_dismissed: 0, individual_test: 0, individual_test_dismissed: 0 });
  const [busy, setBusy] = useState(null);
  const [dismissing, setDismissing] = useState(null);
  const [dismissReason, setDismissReason] = useState("");
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [remindingGroup, setRemindingGroup] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (uid) => setExpanded((e) => ({ ...e, [uid]: !e[uid] }));

  const viewPrimaryDoc = async (licenseId) => {
    try {
      const res = await api.get(`/admin/licenses/${licenseId}/document`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't open the document."); }
  };

  const viewAddlDoc = async (applicationId, licenseId) => {
    try {
      const res = await api.get(`/admin/business/applications/${applicationId}/licenses/${licenseId}/document`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't open the document."); }
  };

  const load = useCallback(() => {
    const p = new URLSearchParams();
    p.set("tab", tab);
    if (!isBiz && blocker) p.set("blocker", blocker);
    if (showDismissed) p.set("show_dismissed", "1");
    // test accounts are always excluded — no reveal path
    setLoadErr(null);
    api.get(`/admin/pending-users?${p.toString()}`)
      .then(({ data }) => { setRows(data.rows || []); setCounts(data.counts || counts); })
      .catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load pending accounts."));
  }, [tab, isBiz, blocker, showDismissed]); // eslint-disable-line

  useEffect(() => {
    if (user && isAdminRole(user.role)) load();
  }, [user, load]);

  if (loading || user === null) return <div className="min-h-screen" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdminRole(user.role)) return <Navigate to="/feed" replace />;

  const activeCount = isBiz ? counts.business : counts.individual;
  const dismissedCount = isBiz ? counts.business_dismissed : counts.individual_dismissed;
  const openInAccounts = (uid) => navigate(`/admin/accounts?open=${uid}`);
  // Group by blocker. Anyone blocked on email (even if also on ID) belongs ONLY to the email
  // group — that's the first thing to resolve. Everyone else (email confirmed) is the ID group.
  const emailRows = rows.filter((r) => (r.blockers || []).includes("email_unverified"));
  const idRows = rows.filter((r) => !(r.blockers || []).includes("email_unverified"));
  const openDismiss = (row) => { setDismissing({ user_id: row.user_id, display_name: row.display_name || row.username }); setDismissReason(""); };

  const remind = async (uid) => {
    setBusy(uid);
    try {
      const { data } = await api.post(`/admin/pending-users/${uid}/remind`);
      toast.success("Reminder email sent.");
      setRows((rs) => rs.map((r) => r.user_id === uid ? { ...r, last_reminder_at: data.last_reminder_at } : r));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const remindAll = async (ids, key) => {
    if (!ids.length) return;
    setRemindingGroup(key);
    try {
      const { data } = await api.post(`/admin/pending-users/remind-all`, { user_ids: ids });
      toast.success(`Reminded ${data.sent}` + (data.throttled ? ` · ${data.throttled} throttled (recently reminded)` : ""));
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setRemindingGroup(null); }
  };

  const submitDismiss = async () => {
    const uid = dismissing.user_id;
    setBusy(uid);
    try {
      await api.post(`/admin/pending-users/${uid}/dismiss`, { reason: dismissReason.trim() });
      toast.success("Removed from the active queue.");
      setDismissing(null); setDismissReason("");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const restore = async (uid) => {
    setBusy(uid);
    try {
      await api.post(`/admin/pending-users/${uid}/restore`);
      toast.success("Returned to the active queue.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const approve = async (appId) => {
    setBusy(appId);
    try {
      await api.post(`/admin/dealer/applications/${appId}/approve`);
      toast.success("Business approved — Dealer badge granted.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const submitReject = async (appId) => {
    if (!rejectReason.trim()) { toast.error("Enter a rejection reason."); return; }
    setBusy(appId);
    try {
      await api.post(`/admin/dealer/applications/${appId}/reject`, { reason: rejectReason.trim() });
      toast.success("Application rejected.");
      setRejecting(null); setRejectReason("");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const Icon = isBiz ? Briefcase : MailCheck;
  const title = isBiz ? "Business Applications" : "Pending Verification";
  const subtitle = isBiz
    ? "Business accounts waiting on YOU to verify. Approve to grant the Business badge + Business founding-seat eligibility, or reject with a reason. Oldest-waiting first."
    : "Members who haven't finished their own onboarding (email / ID / payout). They're waiting on themselves — the only action here is a nudge. Oldest-waiting first.";

  return (
    <div className="text-foreground" data-testid={`pending-${mode}-page`}>
      <div className="max-w-[1180px] mx-auto px-4 py-6">
        <div className="flex items-center gap-2">
          <Icon className="w-6 h-6 text-olive-bright" strokeWidth={1.6} />
          <h1 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="pending-title">
            {title}
            {!(isBiz && bizTab === "waitlist") && (
              <span className="ml-3 font-mono text-lg text-muted-foreground align-middle" data-testid="pending-active-count">{activeCount}</span>
            )}
          </h1>
        </div>
        <p className="text-sm font-body text-secondary-foreground mt-2 max-w-2xl">{isBiz && bizTab === "waitlist" ? "Leads from the public Business & Dealer waitlist form. No approval workflow — just a list to work from manually. Newest first." : subtitle}</p>
        {isBiz && (
          <div className="mt-4 flex gap-2" data-testid="business-tabs">
            <button onClick={() => setBizTab("applications")} data-testid="business-tab-applications"
              className={`px-4 py-2 font-mono uppercase text-xs border transition-colors ${bizTab === "applications" ? "border-olive-bright text-olive-bright bg-olive/10" : "border-sniper text-secondary-foreground hover:text-foreground"}`}>Applications</button>
            <button onClick={() => setBizTab("waitlist")} data-testid="business-tab-waitlist"
              className={`px-4 py-2 font-mono uppercase text-xs border transition-colors ${bizTab === "waitlist" ? "border-olive-bright text-olive-bright bg-olive/10" : "border-sniper text-secondary-foreground hover:text-foreground"}`}>Waitlist</button>
          </div>
        )}
        <Crosshair className="my-5" />

        {/* filters */}
        {!(isBiz && bizTab === "waitlist") && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button onClick={() => setShowDismissed((v) => !v)} data-testid="pending-show-dismissed"
            className={`inline-flex items-center gap-1.5 border px-3 py-2 font-mono uppercase text-xs transition-colors ${showDismissed ? "border-olive-bright text-olive-bright bg-olive/10" : "border-sniper text-secondary-foreground hover:text-foreground"}`}>
            <EyeOff className="w-3.5 h-3.5" /> Dismissed ({dismissedCount})
          </button>
        </div>
        )}

        {/* business waitlist leads (new lead-capture tab) */}
        {isBiz && bizTab === "waitlist" && <BusinessWaitlistTable />}

        {/* business applications table (verification mode uses the grouped layout below) */}
        {isBiz && bizTab === "applications" && (
          loadErr ? (
            <div className="mt-5 border border-sniper bg-surface" data-testid="pending-table"><LoadFailed reason={loadErr} onRetry={load} testid="pending-load-failed" /></div>
          ) : rows.length === 0 ? (
            <div className="mt-5 border border-sniper bg-surface p-10 text-center" data-testid="pending-empty">
              <Icon className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" strokeWidth={1.4} />
              <p className="font-heading uppercase tracking-tight text-lg text-foreground">{showDismissed ? "No dismissed applications" : "No business applications waiting"}</p>
              <p className="font-mono text-[11px] text-muted-foreground mt-1 max-w-md mx-auto">{showDismissed ? "Nothing has been dismissed from this queue." : "When a business signs up and submits its details, its application lands here for you to review and approve. There's nothing to act on right now."}</p>
              {!showDismissed && dismissedCount > 0 && (
                <button onClick={() => setShowDismissed(true)} data-testid="pending-empty-show-dismissed" className="mt-4 inline-flex items-center gap-1.5 border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-4 py-2 transition-colors"><EyeOff className="w-3.5 h-3.5" /> View {dismissedCount} dismissed</button>
              )}
            </div>
          ) : (
          <div className="mt-5 border border-sniper bg-surface sm:overflow-x-auto" data-testid="pending-table">
          <table className="w-full text-left border-collapse block sm:table sm:min-w-[900px]">
            <thead className="hidden sm:table-header-group">
              <tr className="border-b border-sniper font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3">Signup</th>
                <th className="px-4 py-3">ID Status</th>
                <th className="px-4 py-3">Blockers</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="block sm:table-row-group">
              {rows.map((r) => {
                const cooling = withinCooldown(r.last_reminder_at);
                const acting = busy === r.user_id || busy === r.application_id;
                return (
                  <React.Fragment key={r.user_id}>
                  <tr className="block sm:table-row border border-sniper sm:border-x-0 sm:border-t-0 sm:border-b sm:border-sniper/60 mb-3 sm:mb-0 hover:bg-white/[0.02] align-top" data-testid={`pending-row-${r.user_id}`}>
                    <td className="block sm:table-cell px-4 py-3">
                      <button type="button" onClick={() => openInAccounts(r.user_id)} title="Open Manage drawer"
                        data-testid={`pending-open-drawer-${r.user_id}`} className="text-left group/acct">
                        {r.display_name && r.display_name.toLowerCase() !== (r.username || "").toLowerCase() && (
                          <div className="font-body text-sm text-foreground group-hover/acct:text-olive-bright transition-colors">{r.display_name}</div>
                        )}
                        <div className="font-mono text-[11px] text-muted-foreground group-hover/acct:text-olive-bright transition-colors">@{r.username}</div>
                        <div className="font-mono text-[11px] text-secondary-foreground break-all group-hover/acct:text-secondary-foreground/80 transition-colors">{r.email}</div>
                      </button>
                      {r.dismissed && (
                        <div className="mt-1 font-mono text-[10px] uppercase text-muted-foreground" data-testid={`pending-dismissed-info-${r.user_id}`}>
                          Dismissed by {r.dismissed_by} · {timeAgo(r.dismissed_at)}{r.dismissed_reason ? ` · ${r.dismissed_reason}` : ""}
                        </div>
                      )}
                    </td>
                    <td className="block sm:table-cell px-4 py-3 font-mono text-[11px] text-secondary-foreground sm:whitespace-nowrap" title={r.created_at}>
                      <span className="sm:hidden text-muted-foreground uppercase mr-1">Signup:</span>
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                      <span className="text-muted-foreground sm:block"> {timeAgo(isBiz ? (r.app_created_at || r.created_at) : r.created_at)}</span>
                    </td>
                    <td className="block sm:table-cell px-4 py-3">
                      <span className="sm:hidden font-mono text-[10px] uppercase text-muted-foreground mr-1">ID:</span>
                      <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border ${ID_TONE[r.id_status] || ID_TONE.unverified}`} data-testid={`id-status-${r.user_id}`}>
                        {r.id_status}
                      </span>
                    </td>
                    <td className="block sm:table-cell px-4 py-3"><BlockerChips blockers={r.blockers} uid={r.user_id} /></td>
                    <td className="block sm:table-cell px-4 py-3">
                      <div className="flex flex-col items-start sm:items-end gap-1.5">
                        <div className="flex flex-wrap justify-start sm:justify-end gap-1.5">
                          {isBiz && !r.dismissed && (
                            <>
                              <button onClick={() => approve(r.application_id)} disabled={acting} data-testid={`pending-approve-${r.application_id}`}
                                className="inline-flex items-center gap-1 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                                <Check className="w-3.5 h-3.5" /> Approve
                              </button>
                              <button onClick={() => { setRejecting(r.application_id); setRejectReason(""); }} disabled={acting} data-testid={`pending-reject-${r.application_id}`}
                                className="inline-flex items-center gap-1 border border-blaze/50 text-blaze hover:bg-blaze/10 disabled:opacity-50 font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                                <X className="w-3.5 h-3.5" /> Reject
                              </button>
                            </>
                          )}
                          <button onClick={() => openInAccounts(r.user_id)} data-testid={`pending-open-accounts-${r.user_id}`}
                            className="inline-flex items-center gap-1 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                            <ExternalLink className="w-3.5 h-3.5" /> Users
                          </button>
                          {isBiz && (
                            <button onClick={() => toggleExpand(r.user_id)} data-testid={`pending-details-${r.application_id}`}
                              className="inline-flex items-center gap-1 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                              {expanded[r.user_id] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Details
                            </button>
                          )}
                          {!isBiz && !r.dismissed && (
                            <button onClick={() => remind(r.user_id)} disabled={acting || cooling} title={cooling ? "A reminder was sent in the last 72h" : "Send onboarding reminder"} data-testid={`pending-remind-${r.user_id}`}
                              className="inline-flex items-center gap-1 border border-sniper hover:border-[#d3a24e] text-secondary-foreground hover:text-[#d3a24e] disabled:opacity-40 disabled:hover:border-sniper disabled:hover:text-secondary-foreground font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                              <MailWarning className="w-3.5 h-3.5" /> Remind
                            </button>
                          )}
                          {!r.dismissed ? (
                            <button onClick={() => { setDismissing({ user_id: r.user_id, display_name: r.display_name || r.username }); setDismissReason(""); }} disabled={acting} data-testid={`pending-dismiss-${r.user_id}`}
                              className="inline-flex items-center gap-1 border border-sniper hover:border-muted-foreground text-muted-foreground hover:text-foreground disabled:opacity-50 font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                              <EyeOff className="w-3.5 h-3.5" /> Dismiss
                            </button>
                          ) : (
                            <button onClick={() => restore(r.user_id)} disabled={acting} data-testid={`pending-restore-${r.user_id}`}
                              className="inline-flex items-center gap-1 border border-olive-bright/50 text-olive-bright hover:bg-olive/10 disabled:opacity-50 font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors">
                              <RotateCcw className="w-3.5 h-3.5" /> Restore
                            </button>
                          )}
                        </div>
                        {r.last_reminder_at && (
                          <span className="font-mono text-[10px] uppercase text-muted-foreground flex items-center gap-1" data-testid={`pending-last-reminder-${r.user_id}`}>
                            <Clock className="w-3 h-3" /> reminded {timeAgo(r.last_reminder_at)}
                          </span>
                        )}
                        {rejecting === r.application_id && (
                          <div className="mt-1 flex items-center gap-1.5 w-full max-w-[320px]" data-testid={`pending-reject-box-${r.application_id}`}>
                            <input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid={`pending-reject-reason-${r.application_id}`}
                              placeholder="Reason…" className="flex-1 bg-input border border-sniper focus:border-olive outline-none px-2 py-1.5 text-foreground text-xs transition-colors" />
                            <button onClick={() => submitReject(r.application_id)} data-testid={`pending-reject-confirm-${r.application_id}`}
                              className="bg-blaze hover:brightness-110 text-black font-mono uppercase text-[10px] px-3 py-1.5 transition-all">OK</button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isBiz && expanded[r.user_id] && (
                    <tr className="block sm:table-row" data-testid={`pending-detail-row-${r.user_id}`}>
                      <td colSpan={5} className="block sm:table-cell px-4 pb-4 pt-0 sm:bg-black/20">
                        <BizAppDetail app={r.application} ffl={r.ffl} onViewPrimaryDoc={viewPrimaryDoc} onViewAddlDoc={viewAddlDoc} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
          )
        )}

        {/* verification mode — grouped by blocker */}
        {!isBiz && (
          <>
            <PVGroup testid="pending-email-group" title="Email not verified"
              desc="Never confirmed their address. Remind before anything else."
              rowsData={emailRows} remindingAll={remindingGroup === "email"}
              onRemindAll={() => remindAll(emailRows.filter((r) => !r.dismissed).map((r) => r.user_id), "email")}>
              {emailRows.map((r) => (
                <PVRow key={r.user_id} r={r} tone={PV.amber} busy={busy}
                  onRemind={remind} onOpenUser={openInAccounts} onRestore={restore} onDismiss={openDismiss} />
              ))}
            </PVGroup>
            <PVGroup testid="pending-id-group" title="ID not verified"
              desc="Email confirmed. They just have not run the ID check."
              rowsData={idRows} remindingAll={remindingGroup === "id"}
              onRemindAll={() => remindAll(idRows.filter((r) => !r.dismissed).map((r) => r.user_id), "id")}>
              {idRows.map((r) => (
                <PVRow key={r.user_id} r={r} tone={PV.gold} busy={busy}
                  onRemind={remind} onOpenUser={openInAccounts} onRestore={restore} onDismiss={openDismiss} />
              ))}
            </PVGroup>
          </>
        )}
      </div>

      {/* dismiss dialog */}
      <Dialog open={!!dismissing} onOpenChange={(v) => { if (!v) setDismissing(null); }}>
        <DialogContent className="bg-surface border-sniper text-foreground rounded-none" data-testid="pending-dismiss-modal">
          <DialogHeader><DialogTitle className="font-heading uppercase flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#d3a24e]" /> Dismiss from queue</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs font-body text-secondary-foreground">
            Hides <span className="text-foreground">{dismissing?.display_name}</span> until their blocker set changes. It stays reachable under “Dismissed”. Add an optional reason for the audit trail.
          </DialogDescription>
          <textarea rows={2} value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} data-testid="pending-dismiss-reason"
            placeholder="Reason (optional)…" className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground transition-colors" />
          <DialogFooter>
            <button onClick={submitDismiss} data-testid="pending-dismiss-confirm"
              className="bg-[#d3a24e] hover:brightness-110 text-black font-heading font-bold uppercase px-6 py-2.5 transition-all">Dismiss</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
